import { useState, useEffect } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { TextField, Button, Typography, CircularProgress } from '@mui/material';
import { useAuth } from '../context/AuthContext';
import api from '../api/client';
import AuthAlert from '../components/AuthAlert';
import AuthCard from '../components/AuthCard';
import PasswordField from '../components/PasswordField';
import PasswordHints from '../components/PasswordHints';
import { t } from '../i18n/errors';
import { useColors } from '../context/ThemeContext';
import TurnstileWidget, { TURNSTILE_ENABLED } from '../components/TurnstileWidget';

export default function Register() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const colors = useColors();
  const [form, setForm] = useState({ email: '', username: '', password: '' });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [captchaToken, setCaptchaToken] = useState('');

  // Preserve redirect param through the registration flow
  const redirect = new URLSearchParams(location.search).get('redirect') || '';

  useEffect(() => {
    if (user) navigate(redirect || '/');
  }, [user, navigate, redirect]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (form.password.length < 8) {
      setError('Le mot de passe doit contenir au moins 8 caractères');
      return;
    }
    if (!/[^A-Za-z0-9]/.test(form.password)) {
      setError('Le mot de passe doit contenir au moins un caractère spécial.');
      return;
    }
    if (!/\d/.test(form.password)) {
      setError('Le mot de passe doit contenir au moins un chiffre.');
      return;
    }
    if (TURNSTILE_ENABLED && !captchaToken) {
      setError('Veuillez valider le captcha anti-robot.');
      return;
    }
    setLoading(true);
    try {
      await api.post('/auth/register/', { ...form, turnstile_token: captchaToken });
      navigate('/check-email', { state: { email: form.email, redirect } });
    } catch (err) {
      const data = err.response?.data || {};
      const msg = data.error || data.email?.[0] || data.username?.[0] || data.password?.[0] || data.non_field_errors?.[0] || "Erreur lors de l'inscription";
      setError(t(msg));
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthCard title="Créez votre compte gratuitement">
      <Typography variant="h6" sx={{ color: colors.textPrimary, textAlign: 'center' }}>Inscription</Typography>

      {error && <AuthAlert>{error}</AuthAlert>}

      <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
        <TextField label="Nom d'utilisateur" required fullWidth autoFocus
          disabled={loading}
          value={form.username} onChange={(e) => setForm({ ...form, username: e.target.value })} />
        <TextField label="Email" type="email" required fullWidth
          disabled={loading}
          value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
        <PasswordField label="Mot de passe (8 caractères min)" required fullWidth
          disabled={loading}
          value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })}
          onKeyDown={(e) => { if (e.key === 'Enter') handleSubmit(e); }} />
        <PasswordHints value={form.password} />
        <TurnstileWidget onVerify={setCaptchaToken} />
        <Button type="submit" variant="contained" color="primary" fullWidth size="large"
          disabled={loading || (TURNSTILE_ENABLED && !captchaToken)}
          sx={{ mt: 1, py: 1.5, fontSize: '1rem', fontWeight: 700 }}>
          {loading ? <CircularProgress size={24} sx={{ color: 'white' }} /> : "S'inscrire"}
        </Button>
      </form>

      <Typography textAlign="center" variant="body2" sx={{ color: colors.textSecondary }}>
        Déjà un compte ?{' '}
        <Link to={`/login${redirect ? '?redirect=' + encodeURIComponent(redirect) : ''}`} style={{ color: colors.primaryLight, fontWeight: 600, textDecoration: 'none' }}>
          Se connecter
        </Link>
      </Typography>

      <Typography textAlign="center" variant="body2">
        <Link to="/" style={{ color: colors.primaryLight, fontWeight: 600, textDecoration: 'none' }}>
          ← Retour à l'accueil
        </Link>
      </Typography>
    </AuthCard>
  );
}
